import React from 'react';
import { Page } from 'equasion-lib';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { zoneQuarantineSuffix } from '@/lib/utils/zoneUtils';
import { unitsLabel } from '@/pages/QuarantineCleanup/quarantineCleanupSteps';
import { displayUpc, toWarehouseProduct, type ProductStock } from './productStock';
import './ScannedProduct.scss';

type ScannedProductProps = {
  code: string;
  products: ProductStock[];
};

/** Quarantined units never count as on hand, so they are called out on their own. */
const quarantinedNote = (quarantined: number) =>
  quarantined > 0 ? ` · ${quarantined} quarantined` : '';

/** A scanned UPC: the product it names, and every zone holding it with each lot's expiration and quantity. */
const ScannedProduct = ({ code, products }: ScannedProductProps) => (
  <Page className="scanned-product action-page">
    <ActionTopBar action="Product Lookup" backPath="/" />
    <div className="action-page-body">
      {!products.length && (
        <p className="scanned-product__empty">
          No product in your catalog uses UPC {displayUpc(code)}.
        </p>
      )}
      {products.map((product) => (
        <section
          key={product.fdaProductId || product.upcProductId}
          className="scanned-product__product"
        >
          <div className="scanned-product__lockup">
            <WarehouseProductDisplay product={toWarehouseProduct(product, code)}>
              {/* An FDA product's identifier is its NDC, so the scanned UPC gets its own line. */}
              {!!product.fdaProductId && (
                <div className="scanned-product__upc">UPC {displayUpc(code)}</div>
              )}
            </WarehouseProductDisplay>
            <div className="scanned-product__total">
              <span>On hand</span>
              <span>
                {unitsLabel(product.stocked)}
                {product.quarantined > 0 && <small>{product.quarantined} quarantined</small>}
              </span>
            </div>
          </div>
          {!product.zones.length && (
            <p className="scanned-product__empty">No stock in any zone.</p>
          )}
          {product.zones.map(({ zone, locationLabel, stocked, quarantined, lots }) => (
            <section key={zone.id} className="scanned-product__zone">
              <h3>
                {zone.name}
                {zoneQuarantineSuffix(zone)}{' '}
                <small>
                  {unitsLabel(stocked)}
                  {quarantinedNote(quarantined)}
                </small>
              </h3>
              {!!locationLabel && (
                <div className="scanned-product__location">{locationLabel}</div>
              )}
              <ul>
                {lots.map((lot) => (
                  <li key={lot.zoneInventoryBalanceId}>
                    <span className="scanned-product__lot">
                      Lot {lot.lot || '—'} · Exp {formatDateOnly(lot.expiration) || '—'}
                    </span>
                    <span className="scanned-product__qty">
                      {lot.stocked}
                      {lot.quarantined > 0 && <small>{lot.quarantined} quarantined</small>}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </section>
      ))}
    </div>
  </Page>
);

export default ScannedProduct;
